import { GFAFullDetails } from "@dune/types";
import { formatDateForInput } from "@dune/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Resolver, useForm } from "react-hook-form";
import z from "zod";

export const gfaAutorisationSchema = z.object({
	type: z.string(),
	number: z.string(),
	issueDate: z.string().optional(),
	displayReportDateOne: z.string().optional(),
	displayReportDateTwo: z.string().optional(),
	displayReportDateThree: z.string().optional(),
});

export const gfaReferenceSchema = z.object({
	section: z.string(),
	number: z.string(),
	locality: z.string(),
	ha: z.coerce.number().optional(),
	a: z.coerce.number().optional(),
	ca: z.coerce.number().optional(),
});

export const gfaAutorisationsAndReferencesSchema = z.object({
	autorisations: z.array(gfaAutorisationSchema),
	references: z.array(gfaReferenceSchema),
});

export type GFAAutorisationsAndReferencesFormData = z.infer<
	typeof gfaAutorisationsAndReferencesSchema
>;

export const gfaAutorisationsAndReferencesDefaultValues: GFAAutorisationsAndReferencesFormData =
	{
		autorisations: [],
		references: [],
	};

export function useGFAAutorisationsAndReferencesForm(
	gfa: GFAFullDetails | null,
) {
	const defaultValues = gfa
		? mapGFAAutorisationsAndReferencesToFormData(gfa)
		: gfaAutorisationsAndReferencesDefaultValues;
	const schema = gfaAutorisationsAndReferencesSchema;
	type FormData = z.infer<typeof schema>;
	const form = useForm<GFAAutorisationsAndReferencesFormData>({
		mode: "all",
		defaultValues,
		resolver: zodResolver(schema) as Resolver<FormData>,
	});
	const { reset } = form;
	useEffect(() => {
		gfa && reset(mapGFAAutorisationsAndReferencesToFormData(gfa));
	}, [reset, gfa]);
	return { form, defaultValues };
}

const mapGFAAutorisationsAndReferencesToFormData = (
	gfa: GFAFullDetails,
): Partial<GFAAutorisationsAndReferencesFormData> => {
	const autorisationsAndReferences = gfa.gfaAutorisationsAndReferences;
	if (!autorisationsAndReferences)
		return gfaAutorisationsAndReferencesDefaultValues;

	const { autorisations, references } = autorisationsAndReferences;

	return {
		autorisations: autorisations.map((autorisation) => ({
			number: autorisation.number ?? "",
			type: autorisation.type ?? "",
			issueDate: formatDateForInput(autorisation.issueDate?.toString()),
			displayReportDateOne: formatDateForInput(
				autorisation.displayReportDateOne?.toString(),
			),
			displayReportDateTwo: formatDateForInput(
				autorisation.displayReportDateTwo?.toString(),
			),
			displayReportDateThree: formatDateForInput(
				autorisation.displayReportDateThree?.toString(),
			),
		})),
		references: references.map((reference) => ({
			number: reference.number ?? "",
			section: reference.section ?? "",
			locality: reference.locality ?? "",
			ha: reference.ha ?? undefined,
			a: reference.a ?? undefined,
			ca: reference.ca ?? undefined,
		})),
	};
};
